// src/app/services/i18n.service.ts
// ════════════════════════════════════════════════════════════════════════════
// DE-6 — translation, done generically.
//
// THE BRIEF: "at least English, Hindi, Telugu, Marathi — but more will come, so build it properly."
// So a new language is DATA: one row in `vd_language`, one set of rows in `vd_ui_string`. No enum to
// edit, no JSON file to ship, no redeploy. A translator can fix a typo at 11pm and every user has it
// on their next page load.
//
// WHY NOT @ngx-translate
//   It is the obvious choice and it is a fine library. But it is not installed, this app is on
//   Angular 14 with an older toolchain, and pulling in a dependency (plus its HTTP loader) to do
//   what is, in the end, a Map lookup against a pack we fetch from our OWN server is a poor trade.
//   What ngx-translate would add on day one is interpolation and pluralisation. We have
//   interpolation below. Pluralisation is not needed to translate a label, and when it IS needed,
//   this service can be swapped for ngx-translate behind the same `| t` pipe without touching a
//   single template.
//
// THE FALLBACK IS THE IMPORTANT PART
//   /api/language/pack merges English UNDERNEATH the chosen language. A key with no translation yet
//   renders in English — never as a raw `dataentry.forms.pick_project`. This matters because a
//   half-translated app is the NORMAL state the day you add a language, and a half-translated app is
//   usable. An app full of dotted keys is not.
// ════════════════════════════════════════════════════════════════════════════
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { apiUrl } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface LanguageRow {
  code: string;
  name: string;
  native_name: string;
  direction: 'ltr' | 'rtl';
}

const STORAGE_KEY = 'vd_lang';
const FALLBACK = 'en';

@Injectable({ providedIn: 'root' })
export class I18nService {

  private apiLink = apiUrl + '/api/';

  /** The active pack: key → translated string. A Map, because the pipe reads it on every CD pass. */
  private strings = new Map<string, string>();

  private _lang = FALLBACK;
  private _languages: LanguageRow[] = [];

  /** Bumped whenever the pack changes. The pipe watches this so templates re-render on switch. */
  readonly changed = new BehaviorSubject<number>(0);

  constructor(private http: HttpClient, private authService: AuthService) {}

  get lang(): string { return this._lang; }
  get languages(): LanguageRow[] { return this._languages; }
  get direction(): 'ltr' | 'rtl' {
    return this._languages.find(l => l.code === this._lang)?.direction || 'ltr';
  }

  /**
   * Called once at app start, BEFORE the user is known.
   *
   * Order matters: localStorage first (the choice the browser remembers), then — once logged in —
   * whatever the server has for this user. A field worker who set Telugu on their phone should not
   * get English for one flash on every load.
   */
  async init(): Promise<void> {
    await this.loadLanguages();

    const remembered = (localStorage.getItem(STORAGE_KEY) || '').toLowerCase();
    const start = this.isKnown(remembered) ? remembered : FALLBACK;
    await this.use(start, { persistToServer: false });
  }

  /** After login: adopt the language stored against the account, if it has one. */
  async syncFromAccount(): Promise<void> {
    try {
      const res: any = await this.post('language/me', {}, true);
      const lang = String(res?.data?.lang || '').toLowerCase();
      if (this.isKnown(lang) && lang !== this._lang) {
        await this.use(lang, { persistToServer: false });
      }
    } catch { /* the user simply keeps whatever they had */ }
  }

  async loadLanguages(): Promise<void> {
    try {
      const res: any = await this.post('language/list', {}, false);
      if (Number(res?.status) === 1 && Array.isArray(res.data) && res.data.length) {
        this._languages = res.data;
        return;
      }
    } catch { /* fall through */ }

    // Never leave the switcher empty — English always exists.
    this._languages = [{ code: 'en', name: 'English', native_name: 'English', direction: 'ltr' }];
  }

  /** Switch language: fetch the pack, remember it, tell the server (if we can). */
  async use(lang: string, opts: { persistToServer?: boolean } = {}): Promise<void> {
    const code = (lang || FALLBACK).toLowerCase();
    if (!this.isKnown(code)) return;

    try {
      const res: any = await this.post('language/pack', { lang: code }, false);
      const map: Record<string, string> = res?.data?.strings || {};

      this.strings = new Map(Object.entries(map));
      this._lang = code;

      localStorage.setItem(STORAGE_KEY, code);
      document.documentElement.setAttribute('lang', code);
      document.documentElement.setAttribute('dir', this.direction);

      this.changed.next(this.changed.value + 1);
    } catch {
      // A failed pack fetch must not black out the app — the UI stays in whatever it was.
      return;
    }

    if (opts.persistToServer !== false) {
      try { await this.post('language/set', { lang: code }, true); } catch { /* best effort */ }
    }
  }

  /**
   * Translate a key. Returns the key itself if it is unknown — which is deliberate and visible:
   * a missing key SHOULD look wrong in development, so it gets added, rather than silently
   * rendering an empty label in production.
   *
   * `params` interpolates {{name}} placeholders:
   *   t('records.count', { n: 12 })  →  "12 records"
   */
  t(key: string, params?: Record<string, any>): string {
    let out = this.strings.get(key);
    if (out === undefined) return key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        out = out!.split(`{{${k}}}`).join(String(v ?? ''));
      }
    }
    return out!;
  }

  has(key: string): boolean {
    return this.strings.has(key);
  }

  private isKnown(code: string): boolean {
    return !!code && this._languages.some(l => l.code === code);
  }

  private async post(path: string, body: any, authed: boolean): Promise<any> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    if (authed) {
      const u = await this.authService.getUserDetails();
      const token = u?.token || '';
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return firstValueFrom(this.http.post(this.apiLink + path, body, { headers }));
  }
}
